/**
 * OpenOrdersPanel — offene Bestellungen (Status „Neu"), unabhängig vom Zeitraum.
 * Verhalten wie zuvor im Dashboard: Zeile öffnet die Bestellung,
 * „Abschließen" ruft die vom Dashboard übergebene Mutation auf.
 */
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { getOrderTotal } from '@/lib/orderFields';
import { Panel, PanelEmpty, PanelSkeleton } from './Panel';
import { eur } from './format';

export default function OpenOrdersPanel({ loading, orders, onOpen, onComplete, completing }) {
  const total = orders.reduce((s, o) => s + getOrderTotal(o), 0);

  return (
    <Panel
      overline="Bearbeitung ausstehend"
      title={`Offene Bestellungen${orders.length ? ` (${orders.length})` : ''}`}
      meta={
        <Link
          to={createPageUrl('AdminOrders')}
          className="inline-flex items-center gap-0.5 h-8 px-2 rounded-md text-xs font-medium text-brand-900 hover:bg-brand-50 transition-colors"
        >
          Alle Aufträge <ChevronRight className="w-4 h-4" />
        </Link>
      }
      bodyClassName="px-0 pb-0"
    >
      {loading ? (
        <div className="px-5 pb-5"><PanelSkeleton rows={4} /></div>
      ) : orders.length === 0 ? (
        <PanelEmpty
          icon={<CheckCircle2 className="w-8 h-8 text-emerald-400" />}
          title="Alle Bestellungen sind bearbeitet"
          hint="Neue Bestellungen aus Außendienst, Portal und Shop erscheinen hier."
        />
      ) : (
        <>
          <ul className="divide-y divide-slate-100 border-t border-slate-100 max-h-[26rem] overflow-y-auto">
            {orders.map((order) => (
              <li
                key={order.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(order)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(order)}
                className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors focus-visible:outline-none focus-visible:bg-slate-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="relative flex w-2 h-2 shrink-0" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-brand-500 opacity-60 motion-safe:animate-ping" />
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-brand-700" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{order.lead_name || 'Unbekannt'}</p>
                    <p className="flex items-center gap-2 text-[11px] text-slate-400 tabular-nums">
                      {order.order_number && <span className="font-mono text-slate-500">{order.order_number}</span>}
                      {order.created_date && format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })}
                      {order.agent_email && <span className="truncate hidden sm:inline">{order.agent_email}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  <span className="text-sm font-semibold text-slate-900 tabular-nums">{eur(getOrderTotal(order))}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onComplete(order.id); }}
                    disabled={completing}
                    aria-label={`Bestellung ${order.order_number || ''} abschließen`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Abschließen</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500 tabular-nums">
            Offenes Volumen <span className="font-semibold text-slate-900">{eur(total)}</span>
          </p>
        </>
      )}
    </Panel>
  );
}
